import { Navigate, Route, Routes } from "react-router-dom";

import { RequireAccess } from "@/app/auth/RequireAccess";
import { findEntryByTo, isEntryRoutable } from "@/app/auth/resolveNavAccess";
import { useNavContext } from "@/app/auth/useNavContext";

import { AccessCatalogProvider } from "./context/AccessCatalogProvider";
import ApiKeysPage from "./pages/ApiKeysPage";
import PoliciesPage from "./pages/PoliciesPage";
import RolesPage from "./pages/RolesPage";

/**
 * Sub-router for `/app/access/*` — the access catalog administered as ENTITIES: roles, the policies they are
 * built from, and the API keys that consume them. Assigning any of it to one person lives in `/app/users`.
 *
 * `roles` comes first, and is the fallback, because it is the only one of the three that is directly
 * assignable: a policy reaches a principal solely through a role, and an API key is authorized solely by the
 * roles it holds. The other two are described in terms of it.
 *
 * The provider wraps the router rather than each page: the three views read each other's catalogs — Policies
 * derives "used by N roles" from the roles catalog, API Keys resolves effective access from it — so mounting
 * it per page would refetch the same roles list three times and let the three drift apart between
 * navigations. `RequireAccess` is per sub-route (not the group guard) because each surface has its own
 * permission; the mounted route in `DashboardNavigation` carries the group guard, as the pair is meant to be
 * used.
 */
/**
 * Landing preference for the module index, most assignable first. Roles leads because it is the only one of
 * the three that is directly assignable to a principal.
 */
const LANDING_ORDER = ["/app/access/roles", "/app/access/policies", "/app/access/api-keys"];

export const AccessNavigation = () => {
  const navContext = useNavContext();

  /**
   * The three surfaces carry DIFFERENT permissions, so the index cannot be a constant: the group guard admits
   * a principal holding any one of them, and a fixed target would then bounce someone who only has
   * `api_key:read` into the Roles page, which denies them — a dead end reached through a door that opened.
   * Each candidate is checked against its OWN entry, never the group.
   */
  const defaultRoute =
    (navContext &&
      LANDING_ORDER.find((to) => {
        const entry = findEntryByTo(to);
        return entry ? isEntryRoutable(entry, navContext) : false;
      })) ??
    "/app/access/roles";

  return (
    <AccessCatalogProvider>
      <Routes>
        <Route
          path="roles"
          element={
            <RequireAccess to="/app/access/roles">
              <RolesPage />
            </RequireAccess>
          }
        />
        <Route
          path="policies"
          element={
            <RequireAccess to="/app/access/policies">
              <PoliciesPage />
            </RequireAccess>
          }
        />
        <Route
          path="api-keys"
          element={
            <RequireAccess to="/app/access/api-keys">
              <ApiKeysPage />
            </RequireAccess>
          }
        />
        <Route path="*" element={<Navigate to={defaultRoute} replace />} />
      </Routes>
    </AccessCatalogProvider>
  );
};
